/**
 * What this window learned about linked files this session (plan 14): a
 * file found missing or out of reach when it was opened or read. The wall
 * never checks on its own; the card says so once Open or the opened view
 * has found out, until the file is located again or reads again.
 */
import { create } from "zustand";
import type { LocalFileProblem } from "@/desktop/local-files";

/**
 * The problems a card shows: the file is gone, this computer has no link
 * for the frame, or macOS keeps it from Denker.
 */
export type LinkProblem = Extract<
  LocalFileProblem,
  "missing" | "unlinked" | "denied"
>;

interface LocalFileProblemState {
  problems: Readonly<Record<string, LinkProblem>>;
  /** Records what a read or an Open of link `linkId` found. */
  report: (linkId: string, problem: LocalFileProblem | null) => void;
}

export const useLocalFileProblemStore = create<LocalFileProblemState>(
  (set) => ({
    problems: {},
    report: (linkId, problem) =>
      set(({ problems }) => {
        const shown =
          problem === "missing" ||
          problem === "unlinked" ||
          problem === "denied"
            ? problem
            : null;
        if ((problems[linkId] ?? null) === shown) return { problems };
        const next = { ...problems };
        if (shown) next[linkId] = shown;
        else delete next[linkId];
        return { problems: next };
      }),
  }),
);

/** The problem known for link `linkId`, or null. */
export function useLinkProblem(linkId: string | null): LinkProblem | null {
  return useLocalFileProblemStore((state) =>
    linkId ? (state.problems[linkId] ?? null) : null,
  );
}
